import analyticstracker from "@jmc/analyticstracker";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjArrowLeft, jnjFilter, jnjSearch } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Tag } from "@jmc/solid-design-system/src/components/atoms/Tag/Tag";
import { TextField } from "@jmc/solid-design-system/src/components/atoms/TextField/TextField";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { mdiArrowLeft, mdiClose, mdiMagnify, mdiTune } from "@mdi/js";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connectHits } from "react-instantsearch-dom";

import { useLocatorContext } from "../LocatorContext/LocatorContext";
import kmToMiles from "../utils/kmToMiles";
import DistanceFilter from "./DistanceFilter";
import SpecialtyFilter from "./SpecialyFilter";
import style from "./style.module.scss";

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    geocoder: (google: any, address: string, distance: string) => void;
    distanceLabel?: "km" | "mi";
    name?: string;
    placement?: string;
    hits: [];
}

const DEFAULT_DISTANCE_M = 20000;

const CustomControls = (props: Props): JSX.Element => {
    const { google, geocoder, distanceLabel = "km", name = "", placement = "", hits } = props;
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const initialDistance = {
        value: distanceLabel === "km" ? DEFAULT_DISTANCE_M.toString() : kmToMiles(DEFAULT_DISTANCE_M).toString(),
        label: `${DEFAULT_DISTANCE_M / 1000}${distanceLabel}`,
    };
    const [address, setAddress] = useState("");
    const { specialtyFilter, setSpecialtyFilter, distanceFilter, setDistanceFilter } = useLocatorContext();
    const [selectedFilterOptions, setSelectedFilterOptions] = useState(0);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    useEffect(() => {
        // Search on distance filter change when there is no search button
        if (google && distanceFilter && address && jnjFullBranded && !mobileFiltersOpen) {
            geocoder(google, address, distanceFilter?.value);
        }
    }, [distanceFilter]);

    useEffect(() => {
        let count = specialtyFilter?.filter((item) => item.isSelected)?.length;
        if (distanceFilter) count++;

        setSelectedFilterOptions(count);
    }, [distanceFilter, specialtyFilter]);

    const resetValues = (): void => {
        setSpecialtyFilter([
            ...specialtyFilter.map((item) => {
                item.deleted = true;
                return item;
            }),
        ]);
        setAddress("");
        setDistanceFilter(null);
        geocoder(google, "", initialDistance.value);
        trackResetFilterButton();
    };

    const trackResetFilterButton = (): void => {
        analyticstracker().trackEvent({
            event: EventTypes.MAP_QUERY,
            info: {
                name: name,
                component: "search",
                placement: placement,
                keyword: "unset",
                resultcount: hits?.length,
                filter: "reset filter",
            },
        });
    };

    const handleKeyPress = (e: KeyboardEvent): void => {
        //it triggers by pressing the enter or spacebar key
        keyHandler(e.key, () => geocoder(google, address, distanceFilter?.value));
    };

    const CleanAddress = (
        <button
            className={style.clear}
            onClick={() => {
                setAddress("");
                geocoder(google, "", distanceFilter.value);
            }}
            aria-label={t("Clear field", { ns: "common" })}
        >
            <Icon color="black" icon={mdiClose} />
        </button>
    );

    const clickSearch = (): void => {
        setMobileFiltersOpen(false);
        geocoder(google, address, distanceFilter?.value);
    };

    return (
        <div className={classnames(style.filters, mobileFiltersOpen ? style.open : null)}>
            <div className={style.header}>
                <button
                    className={style.back}
                    onClick={() => setMobileFiltersOpen(false)}
                    title={t("Back", { ns: "common" })}
                >
                    <Icon
                        color="black"
                        size={jnjFullBranded ? undefined : "large"}
                        icon={jnjFullBranded ? jnjArrowLeft : mdiArrowLeft}
                        verticalAlign="middle"
                    />
                </button>
                <Typography
                    className={style.title}
                    color={jnjFullBranded ? "dark" : "primary"}
                    size={jnjFullBranded ? "xl" : "3xl"}
                    weight={jnjFullBranded ? "400" : "500"}
                    font="title"
                >
                    {t("Filters", { ns: "common" })}
                </Typography>
                <button
                    className={style.reset}
                    onClick={() => resetValues()}
                    aria-label={t("Clear all", { ns: "common" })}
                >
                    <Typography color={jnjFullBranded ? "inherit" : "title"} variant="body">
                        {t("Clear all", { ns: "common" })}
                    </Typography>
                </button>
            </div>
            <div className={style.formWrapper}>
                <div className={style.addressWrapper}>
                    <TextField
                        name="address"
                        value={address}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAddress(event.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={t("Address", { ns: "common" })}
                        endAdornment={CleanAddress}
                        startAdornment={
                            <Icon
                                color={jnjFullBranded ? "primary" : "text-grey"}
                                icon={jnjFullBranded ? jnjSearch : mdiMagnify}
                                type={jnjFullBranded ? "jnj" : "mdi"}
                            />
                        }
                        aria-label={t("Address", { ns: "common" })}
                    />
                </div>

                {jnjFullBranded ? (
                    <div className={style.dropdownsWrapper}>
                        <div className={style.fieldWrapper}>
                            <DistanceFilter distanceLabel={distanceLabel} />
                        </div>

                        <div className={style.fieldWrapper}>
                            <SpecialtyFilter attribute="specialty" />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={style.fieldWrapper}>
                            <DistanceFilter distanceLabel={distanceLabel} />
                        </div>

                        <div className={style.fieldWrapper}>
                            <SpecialtyFilter attribute="specialty" />
                        </div>
                    </>
                )}

                <div className={style.controlButtons}>
                    <div className={style.mobileToggle}>
                        <Button
                            color={jnjFullBranded ? "primary" : "white"}
                            variant={jnjFullBranded ? "outlined" : undefined}
                            size={jnjFullBranded ? "small" : "medium"}
                            fullWidth={true}
                            onClick={() => setMobileFiltersOpen(true)}
                        >
                            <Typography color={jnjFullBranded ? "primary-general" : "primary"} size="m">
                                <Icon
                                    className={style.filterModalButtonIcon}
                                    color="inherit"
                                    icon={jnjFullBranded ? jnjFilter : mdiTune}
                                    type={jnjFullBranded ? "jnj" : "mdi"}
                                />
                                {t("Filters", { ns: "common" })} ({selectedFilterOptions})
                            </Typography>
                        </Button>
                    </div>

                    <div className={style.searchButton}>
                        <Button color="primary" size={jnjFullBranded ? "small" : "medium"} onClick={clickSearch}>
                            <Typography color="white" weight={jnjFullBranded ? "400" : "600"}>
                                {t("Search", { ns: "common" })}
                            </Typography>
                        </Button>
                    </div>
                </div>
            </div>
            {!jnjFullBranded && (
                <div className={style.filterTags}>
                    <fieldset
                        className={classnames(style.fieldset)}
                        aria-labelledby={t("Selected filters", { ns: "common" })}
                    >
                        {distanceFilter && (
                            <Tag color="primary" dismissable={true} onClick={() => setDistanceFilter(null)}>
                                <Typography size="xs" weight="400" color="white">
                                    {distanceFilter.label}
                                </Typography>
                            </Tag>
                        )}
                        {specialtyFilter &&
                            specialtyFilter?.map((item, index) => {
                                const tagLabel = item.label?.replace(/\(.*\)/, "");
                                if (item.isSelected) {
                                    return (
                                        <Tag
                                            color="primary"
                                            dismissable={true}
                                            key={index}
                                            onClick={() => {
                                                const newValues = specialtyFilter;
                                                newValues[index].deleted = true;
                                                setSpecialtyFilter([...newValues]);
                                            }}
                                        >
                                            <Typography size="xs" weight="400" color="white">
                                                {tagLabel}
                                            </Typography>
                                        </Tag>
                                    );
                                } else {
                                    return null;
                                }
                            })}
                    </fieldset>
                    <button
                        className={style.resetButton}
                        onClick={() => resetValues()}
                        aria-label={t("Clear all", { ns: "common" })}
                    >
                        <Typography color={jnjFullBranded ? "inherit" : "title"} variant="body">
                            {t("Clear all", { ns: "common" })}
                        </Typography>
                    </button>
                </div>
            )}
        </div>
    );
};

export const Controls = connectHits(CustomControls);
